import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, Scene } from "./types";

// Lesson: Getting started → Export your report.
// Fixture: `exportYourReport` (the finished Valley Mart report with three
// redactions, a transcript, and the agency "Delete case upon export" policy).
// The scene opens menus and dialogs but never clicks an export button.
const EXPORT_CASE_ID = "c0000000-0000-4000-8000-000000000301";

const MENU = `${sel("editorExport")} + ul`;
const item = (n: number) => `${MENU} > li:nth-child(${n}) p`;
const COPY = item(1);
const WORD = item(2);
const PDF = item(3);
const EMAIL = item(4);
const TRANSCRIPTION = item(5);

const dlg = (key: "exportClipboardDialog" | "exportEmailDialog" | "exportWordDialog" | "exportPdfDialog" | "exportTranscriptionDialog") =>
  sel(key);
const box = (key: Parameters<typeof dlg>[0]) => `${dlg(key)} .modal-box`;
// X button of the clipboard and Word dialogs; Cancel of the others.
const closeX = (key: Parameters<typeof dlg>[0]) => `${dlg(key)} form button`;
const cancel = (key: Parameters<typeof dlg>[0]) => `${dlg(key)} .modal-action button`;

const scene: Scene = {
  id: "export-your-report",
  title: "Export your report",
  startRoute: `/cases/${EXPORT_CASE_ID}`,
  fixture: "exportYourReport",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "Where the report is going decides how you export it. This is the finished Valley Mart report, and the Export button sits at the right end of the editor toolbar.",
      actions: [
        { type: "waitFor", target: sel("editorExport") },
        { type: "wait", ms: 4300 },
        { type: "moveTo", target: sel("editorExport") },
        { type: "spotlight", target: sel("editorExport") },
        { type: "wait", ms: 3500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "menu",
      narration:
        "Click Export and the menu opens. Copy to Clipboard, Word Document, PDF Document, and E-mail, plus Transcription when the case has a recording.",
      actions: [
        { type: "wait", ms: 300 },
        { type: "click", target: sel("editorExport") },
        { type: "waitFor", target: COPY },
        { type: "wait", ms: 400 },
        { type: "spotlight", target: MENU },
        { type: "wait", ms: 7000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "copy",
      narration:
        "Most officers just use Copy to Clipboard. Read the acknowledgment, agree, and the report is on your clipboard, ready to paste into your records system.",
      actions: [
        { type: "wait", ms: 900 },
        { type: "click", target: COPY },
        { type: "waitFor", target: box("exportClipboardDialog") },
        { type: "spotlight", target: box("exportClipboardDialog") },
        { type: "wait", ms: 4600 },
        { type: "spotlight", target: null },
        { type: "click", target: closeX("exportClipboardDialog") },
        { type: "wait", ms: 400 },
      ],
    },
    {
      id: "pdf",
      narration:
        "For an attachment, pick PDF Document. You can add a title, a case ID, and dates, put text in the page header and footer, and choose whether to keep your formatting. Then click Agree and download PDF.",
      actions: [
        { type: "click", target: sel("editorExport") },
        { type: "waitFor", target: PDF },
        { type: "click", target: PDF },
        { type: "waitFor", target: box("exportPdfDialog") },
        { type: "wait", ms: 800 },
        { type: "spotlight", target: `${dlg("exportPdfDialog")} [id="toggle-include-title"]` },
        { type: "wait", ms: 1300 },
        { type: "spotlight", target: `${dlg("exportPdfDialog")} [id="toggle-include-case-id"]` },
        { type: "wait", ms: 1100 },
        { type: "spotlight", target: `${dlg("exportPdfDialog")} [id="toggle-include-date"]` },
        { type: "wait", ms: 1000 },
        { type: "scrollIntoView", target: `${dlg("exportPdfDialog")} textarea` },
        { type: "spotlight", target: `${dlg("exportPdfDialog")} textarea` },
        { type: "wait", ms: 1800 },
        { type: "spotlight", target: `${dlg("exportPdfDialog")} [id="toggle-preserve-formatting"]` },
        { type: "scrollIntoView", target: `${dlg("exportPdfDialog")} [id="toggle-preserve-formatting"]` },
        { type: "wait", ms: 2000 },
        { type: "scrollIntoView", target: `${dlg("exportPdfDialog")} .modal-action` },
        { type: "spotlight", target: `${dlg("exportPdfDialog")} .modal-action button:last-child` },
        { type: "wait", ms: 2200 },
        { type: "spotlight", target: null },
        { type: "click", target: cancel("exportPdfDialog") },
        { type: "wait", ms: 400 },
      ],
    },
    {
      id: "word-email",
      narration:
        "Word Document gives you the same report as a Word file. E-mail sends it straight to the address you type in.",
      actions: [
        { type: "click", target: sel("editorExport") },
        { type: "waitFor", target: WORD },
        { type: "click", target: WORD },
        { type: "waitFor", target: box("exportWordDialog") },
        { type: "wait", ms: 400 },
        { type: "click", target: closeX("exportWordDialog") },
        { type: "wait", ms: 100 },
        { type: "click", target: sel("editorExport") },
        { type: "waitFor", target: EMAIL },
        { type: "click", target: EMAIL },
        { type: "waitFor", target: box("exportEmailDialog") },
        { type: "type", target: `${dlg("exportEmailDialog")} input[type="email"]`, text: "supervisor@example.gov", cps: 14 },
        { type: "wait", ms: 800 },
        { type: "click", target: cancel("exportEmailDialog") },
        { type: "wait", ms: 400 },
      ],
    },
    {
      id: "transcription",
      narration:
        "Transcription exports the transcript on its own, as a PDF, Word, or text file. You can include timestamps and add a header.",
      actions: [
        { type: "click", target: sel("editorExport") },
        { type: "waitFor", target: TRANSCRIPTION },
        { type: "click", target: TRANSCRIPTION },
        { type: "waitFor", target: box("exportTranscriptionDialog") },
        { type: "wait", ms: 600 },
        { type: "spotlight", target: `${dlg("exportTranscriptionDialog")} .dropdown > button` },
        { type: "wait", ms: 2800 },
        { type: "spotlight", target: `${dlg("exportTranscriptionDialog")} [id="toggle-include-timestamps"]` },
        { type: "wait", ms: 1300 },
        { type: "scrollIntoView", target: `${dlg("exportTranscriptionDialog")} textarea` },
        { type: "spotlight", target: `${dlg("exportTranscriptionDialog")} textarea` },
        { type: "wait", ms: 1800 },
        { type: "spotlight", target: null },
        { type: "click", target: cancel("exportTranscriptionDialog") },
        { type: "wait", ms: 400 },
      ],
    },
    {
      id: "redactions",
      narration:
        "Every export dialog also has Export with redactions, which covers flagged text with black bars. And if you see Delete case upon export checked, that's your department's retention policy, so confirm the export worked before you close the tab.",
      actions: [
        { type: "click", target: sel("editorExport") },
        { type: "waitFor", target: COPY },
        { type: "click", target: COPY },
        { type: "waitFor", target: box("exportClipboardDialog") },
        { type: "click", target: `${dlg("exportClipboardDialog")} [id^="toggle-export-with-redactions"]` },
        { type: "wait", ms: 1000 },
        { type: "spotlight", target: `${dlg("exportClipboardDialog")} .not-prose.grid` },
        { type: "wait", ms: 2800 },
        { type: "spotlight", target: `${dlg("exportClipboardDialog")} .border-red-200` },
        { type: "wait", ms: 5200 },
        { type: "spotlight", target: null },
        { type: "click", target: closeX("exportClipboardDialog") },
        { type: "wait", ms: 400 },
      ],
    },
    {
      id: "outro",
      narration:
        "One last thing. Clear any placeholders first, because an exported report still contains them. Then pick the export that fits where the report is going.",
      actions: [
        { type: "wait", ms: 5800 },
        { type: "moveTo", target: sel("editorExport") },
        { type: "spotlight", target: sel("editorExport") },
        { type: "wait", ms: 2500 },
        { type: "spotlight", target: null },
      ],
    },
  ],
};

export default scene;
